/**
 * The region field, the region types that are not their own file, and the boundary wall.
 *
 * Defaults come from DEFAULT_REGION_CONFIG; catalog.test.ts holds this file to them.
 *
 * A REGION TYPE IS A NODE, and its share of the world is a parameter ON it. `regions.typeWeights`
 * used to be a three-way weights control and `meadow.scatterRegions` a three-way multi-select —
 * both of them lists of region types written BESIDE the tree while the tree already had two of the
 * three as children. Now `wild` is a node too, each type carries its own `frequency`, and the
 * editor divides them proportionally (see the `share` unit). Nothing declares which types scatter:
 * a type scatters where it has scatter children, which meadow does and the other two do not.
 */
export const REGIONS = {
    kind: 'regions',
    label: 'Regions',
    blurb: 'The world divides into regions on a warped cellular diagram, each outlined by a wall in '
        + 'its own type’s colour. On flat ground the walls ARE the region map.',
    capability: 'group',
    maxLod: -1,
    expandAtLod: 99,
    // THE WALL IS LAST BECAUSE IT COMPOSES LAST. A child's position in the tree IS its composition
    // order (the plan's §2.3), and the boundary wall gets the last word over the city mass it meets:
    // it is the one landmark the whole region system is read from, and a tower drawn over it reads as
    // a generation bug from 300 m away. Declared wall-first, the tree said the opposite of what the
    // writer does — caught by columnPhases.test.ts, which is the only reason anyone would notice.
    children: ['meadow', 'wild', 'city', 'wall'],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'cellSize', label: 'Region size', min: 100, max: 3000, step: 50, def: 200, unit: 'm',
            desc: 'Width of one region in meters. Lower = many small regions; higher = a few large.' },
        { t: 'number', key: 'jitter', label: 'Irregularity', min: 0, max: 50, step: 2.5, def: 50, unit: '%',
            desc: 'How far a region centre wanders in its cell. 0 = a regular grid; 50 = most irregular.' },
        // Wander scales WITH this, so it changes the size of a wiggle and not its character. Measured
        // at maximum wander: detail 2 and 4 miss zero columns at every level, 6 misses 19, 24 misses
        // 248 — a coarse chunk cannot resolve a wiggle finer than its own cell. Raise Region size to
        // go further. The same measurement is in RegionConfig.edgeDetail's own note.
        { t: 'number', key: 'edgeDetail', label: 'Edge detail', min: 0.25, max: 8, step: 0.25, def: 2,
            desc: 'Wiggles per region along a boundary. Higher = finer kinks; above ~4 far walls nick.' },
        // 0.5 is the clamp, not a taste: past it the warp folds space and boundaries self-intersect.
        { t: 'number', key: 'edgeWander', label: 'Edge wander', min: 0, max: 50, step: 1, def: 20, unit: '%',
            desc: 'How far a boundary bends, as a fraction of one wiggle. 0 = clean polygon edges.' },
    ],
};
export const WILD = {
    kind: 'wild',
    label: 'Wild',
    blurb: 'Bare ground between the meadows and the cities. Nothing scatters here — give it scatter '
        + 'children and it will, with no other change anywhere.',
    capability: 'group',
    maxLod: -1,
    expandAtLod: -1,
    children: [],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'frequency', label: 'Frequency', share: true, min: 0, max: 100, step: 5, def: 30, unit: '%',
            desc: 'How common this region type is, against the others. Unticking it is a frequency of zero.' },
        { t: 'material', key: 'wallMaterial', label: 'Wall material', def: 'stone2',
            desc: 'The colour this type’s stretch of boundary wall is drawn in.' },
    ],
};
export const WALL = {
    kind: 'wall',
    label: 'Boundary wall',
    blurb: 'A wall along every region boundary, in the nearest region’s own colour. Held at its true '
        + 'width up close and never thinner than a distant chunk can draw, so it stays continuous at '
        + 'every range.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: -1,
    children: [],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'width', label: 'Wall width', min: 1, max: 24, step: 1, def: 1, unit: 'm',
            desc: 'Wall thickness up close. Distant walls are always at least one voxel wide.' },
        { t: 'number', key: 'height', label: 'Wall height', min: 0, max: 50, step: 1, def: 10, unit: 'm',
            desc: 'Wall height above the ground it stands on. 0 = no walls.' },
    ],
};
export const PATHS = {
    kind: 'paths',
    label: 'Paths',
    blurb: 'A path from each region’s node to its neighbours’, routed by least resistance: straight '
        + 'over flat land, switchbacking up steep ground, around the water. The ground benches flat '
        + 'across it.',
    capability: 'field',
    maxLod: 99,
    expandAtLod: -1,
    children: [],
    toggle: { key: 'enabled', def: true },
    params: [
        { t: 'number', key: 'width', label: 'Width', min: 1, max: 10, step: 0.5, def: 3, unit: 'm',
            desc: 'The paved ribbon. The flattening extends an apron beyond it.' },
        { t: 'number', key: 'apron', label: 'Apron', min: 0, max: 12, step: 0.5, def: 4, unit: 'm',
            desc: 'How far past the ribbon the bench fades back into the terrain.' },
        { t: 'number', key: 'grade', label: 'Flatness', min: 0, max: 100, step: 5, def: 30,
            desc: 'How hard routes avoid steepness. Higher = flatter, longer switchbacks; 0 = beelines.' },
        { t: 'number', key: 'wander', label: 'Wander', min: 25, max: 300, step: 25, def: 150, unit: 'm',
            desc: 'How far a route may stray to get around obstacles. Lower is more direct and faster.' },
    ],
};
export const REGION_KINDS = [REGIONS, WILD, PATHS, WALL];
//# sourceMappingURL=regions.js.map